import {
  Box,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCountDown } from "features/Home/Banner/useCountdown";
import { UpgradeDialog } from "features/Home/UpgradeDialog";
import { NotInterested } from "features/Home/UpgradeDialog/NotInterested";
import OnboardingPaywall from "features/Home/UpgradeDialog/OnboardingPaywall";
import OnboardingPaywallMobile from "features/Home/UpgradeDialog/OnboardingPaywall/mobile";
import {
  DISPLAYED_ONBOARDING_PAYWALL,
  FEEDBACK_GIVEN,
} from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import StyledOnboardingPage from "../StyledOnboardingPage";
import { getOnboardingPage } from "../helper";

interface Props {
  handleSkip: () => void;
  moveNext: () => void;
}

const OnboardingPaywallPage = ({ handleSkip, moveNext }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { setAlert } = useContext(AlertContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [notInterestedOpen, setNotInterestedOpen] = useState(false);

  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, fetchPlans } = useContext(SubscriptionContext);
  const {
    upgradeDialogSource,
    setUpgradeDialogSource,
    setOpenCheckoutDialog,
    subscription,
  } = useContext(SubscriptionContext);
  const { display, createTimer } = useCountDown();
  const hasAlreadyGivenFeedback =
    localStorage.getItem(`${FEEDBACK_GIVEN}-${currentOrg?.id}`) === "true";
  const handleCloseUpgradeDialog = () => {
    if (!hasAlreadyGivenFeedback) {
      setNotInterestedOpen(true);
    }
    setUpgradeDialogSource(null);
  };

  const handleCloseNotInterestedDialog = () => {
    trackEvent("Onboarding Paywall - Not Interested Dialog Closed");
    const givenFeedback =
      localStorage.getItem(`${FEEDBACK_GIVEN}-${currentOrg?.id}`) === "true";
    if (givenFeedback) {
      setUpgradeDialogSource(null);
      if (upgradeDialogSource === UpgradeDialogSource.onboarding_paywall) {
        localStorage.setItem(
          `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
          "true",
        );
      }
    }
    setNotInterestedOpen(false);
    searchParams.delete("checkout");
    setSearchParams(searchParams);
    if (upgradeDialogSource) setUpgradeDialogSource(null);
  };

  useEffect(() => {
    createTimer();
    if (!plans && currentOrg?.id) {
      fetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, currentOrg?.id]);

  useEffect(() => {
    trackEvent("Shown Paywall Dialog", {
      Page: getOnboardingPage(location.pathname),
      Reason: UpgradeDialogSource.onboarding_paywall,
    });
    if (currentOrg?.id) {
      localStorage.setItem(
        `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
        "true",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <StyledOnboardingPage
      title="Let's pick a plan"
      description={
        <>
          Unlock all features and start landing more partnerships.{" "}
          {subscription?.bannerPromoCode?.discountPercent && display && (
            <Typography sx={{ my: 2, fontSize: 16 }}>
              👉{" "}
              <em>
                {" "}
                Your special {subscription?.bannerPromoCode?.discountPercent}%
                discount ends in <strong>{display}</strong>
              </em>
            </Typography>
          )}
        </>
      }
      nextButtonText={currentOrg?.isPaidUser ? "Next" : "Upgrade"}
      handleNext={
        isMobileScreen
          ? undefined
          : () => {
              trackEvent("Onboarding Form - Next Button Pressed", {
                page: getOnboardingPage(location.pathname),
                route: location.pathname,
              });
              if (currentOrg?.isPaidUser) {
                moveNext();
                return;
              }
              setUpgradeDialogSource(UpgradeDialogSource.onboarding_paywall);

              const state: CheckoutState = {
                plan: Plans.PRO,
                source: "Onboarding Paywall",
              };
              setOpenCheckoutDialog(state);
            }
      }
      handleSkip={isMobileScreen ? undefined : handleSkip}
    >
      <>
        {!plans && (
          <Skeleton
            variant="rounded"
            animation="wave"
            width="100%"
            height={500}
            sx={{ mb: 3 }}
          />
        )}
        {plans && (
          <>
            {isMobileScreen ? (
              <OnboardingPaywallMobile
                closeDialog={() => {
                  trackEvent("Onboarding Form - Skip Pressed", {
                    page: getOnboardingPage(location.pathname),
                    route: location.pathname,
                  });
                  handleSkip();
                }}
              />
            ) : (
              <Box
                sx={{
                  mb: 4,
                  px: 3,
                  py: 4,
                  minWidth: { xs: "auto", md: "50vw", lg: "1200px" },

                  borderRadius: 4,
                  backgroundColor: "white",
                }}
              >
                <OnboardingPaywall />
              </Box>
            )}
          </>
        )}
      </>

      <UpgradeDialog
        open={!!upgradeDialogSource}
        onClose={handleCloseUpgradeDialog}
        setAlert={setAlert}
      />
      <NotInterested
        open={notInterestedOpen || searchParams.get("checkout") === "failed"}
        close={handleCloseNotInterestedDialog}
        setAlert={setAlert}
      />
    </StyledOnboardingPage>
  );
};

export default OnboardingPaywallPage;
